import * as THREE from 'three';
import { CREATURE_DEFS, WORLD, levelCurve, type CreatureKind } from './config';
import { stageForLevel } from './stages';
import { createCreatureSprite } from './pixelAssets';
import type { Mineral } from './minerals';
import type { SeededRandom } from '../utils/random';

export interface Creature {
  id:number;kind:CreatureKind;x:number;y:number;anchorX:number;anchorY:number;
  phase:number;scared:number;cooldown:number;warning:number;hiddenFor:number;
  target:Mineral|null;held:Mineral|null;sprite:THREE.Sprite;marker:THREE.Mesh<THREE.RingGeometry,THREE.MeshBasicMaterial>;
}
export class CreatureManager {
  readonly creatures:Creature[]=[];
  private elapsed=0;
  constructor(private readonly scene:THREE.Scene) {}
  reset(level:number,rng:SeededRandom,minerals:Mineral[],twoPlayer:boolean):void {
    this.clear();this.elapsed=0;
    const stage=stageForLevel(level),count=levelCurve(level).creatures+(twoPlayer&&level>=5?1:0);
    for(let i=0;i<count;i++){
      const kind=stage.creatures[i%stage.creatures.length];
      const x=(i-(count-1)/2)*4.4+(rng()-.5),y=-2.7-i*2.6;
      const sprite=createCreatureSprite(kind,CREATURE_DEFS[kind].radius*2.35);sprite.position.set(x,y,.4);
      const marker=new THREE.Mesh(new THREE.RingGeometry(.75,.8,24),new THREE.MeshBasicMaterial({color:'#e9b65e',transparent:true,opacity:.8,depthWrite:false}));
      marker.visible=false;marker.position.z=.3;this.scene.add(sprite,marker);
      this.creatures.push({id:i+1,kind,x,y,anchorX:x,anchorY:y,phase:rng()*6.28,scared:0,cooldown:5+i*2,warning:0,hiddenFor:0,target:null,held:null,sprite,marker});
    }
    // The initial patrol positions are independent of resource value and cannot consume income.
    void minerals;
  }
  private release(c:Creature):void {
    if(c.held){c.held.hiddenBy=null;c.held.group.visible=c.held.active;c.held=null;}
    c.target=null;c.warning=0;c.hiddenFor=0;c.marker.visible=false;
  }
  scareAll(duration=6):void {for(const c of this.creatures){this.release(c);c.scared=duration;c.cooldown=7;}}
  scareNear(x:number,y:number,radius:number):void {for(const c of this.creatures)if(Math.hypot(c.x-x,c.y-y)<radius){this.release(c);c.scared=6;c.cooldown=7;}}
  update(dt:number,motionScale:number,minerals:Mineral[],reducedMotion:boolean):void {
    this.elapsed+=dt*motionScale;
    for(const c of this.creatures){
      c.scared=Math.max(0,c.scared-dt);c.sprite.visible=c.scared<=0;
      if(c.scared>0){c.marker.visible=false;continue;}
      const def=CREATURE_DEFS[c.kind];
      const previousX=c.x;
      c.x=Math.max(-WORLD.halfWidth+1,Math.min(WORLD.halfWidth-1,c.anchorX+Math.sin(this.elapsed*def.speed+c.phase)*(c.kind==='bat'?3.2:2.2)));
      c.y=c.anchorY+Math.sin(this.elapsed*(c.kind==='bat'?1.9:1.1)+c.phase)*(c.kind==='bat'?.65:.15);
      c.sprite.position.set(c.x,c.y,.4);c.sprite.material.rotation=reducedMotion?0:Math.sin(this.elapsed*3+c.phase)*.035;
      c.sprite.scale.x=Math.abs(c.sprite.scale.x)*(c.x<previousX?-1:1);
      if(c.kind!=='mole')continue;
      if(c.held){
        c.hiddenFor-=dt;if(!c.held.active||c.hiddenFor<=0){this.release(c);c.cooldown=8;}
        continue;
      }
      if(c.target){
        if(!c.target.active||c.target.grabbedBy!==null||c.target.hiddenBy!==null){this.release(c);c.cooldown=5;continue;}
        c.warning-=dt*motionScale;c.marker.position.set(c.target.x,c.target.y,.3);c.marker.visible=true;
        c.marker.material.opacity=.45+.35*Math.sin(this.elapsed*9)**2;
        if(c.warning<=0){c.held=c.target;c.held.hiddenBy=c.id;c.held.group.visible=false;c.target=null;c.hiddenFor=6;c.marker.visible=false;}
        continue;
      }
      c.cooldown-=dt*motionScale;
      if(c.cooldown<=0){
        const target=minerals.filter(m=>m.active&&m.combo!=='none'&&m.hiddenBy===null&&m.grabbedBy===null&&Math.hypot(m.x-c.x,m.y-c.y)<3.6)
          .sort((a,b)=>b.value-a.value)[0];
        if(target){c.target=target;c.warning=3;}else c.cooldown=2;
      }
    }
  }
  /** Called only for an empty outgoing hook, after its ordinary mineral collision. */
  hit(x:number,y:number,shield:boolean):{kind:CreatureKind;delay:number}|null {
    const c=this.creatures.find(c=>c.scared<=0&&Math.hypot(c.x-x,c.y-y)<CREATURE_DEFS[c.kind].radius+.18);
    if(!c)return null;
    this.release(c);c.scared=6;c.cooldown=7;c.sprite.visible=false;
    return {kind:c.kind,delay:shield?0:CREATURE_DEFS[c.kind].delay};
  }
  get message():string {
    const hiding=this.creatures.filter(c=>c.held).length;
    const warning=this.creatures.find(c=>c.target);
    if(warning)return `鼹鼠盯上矿物 · ${Math.ceil(warning.warning)} 秒内出钩或摇铃`;
    if(hiding)return `${hiding} 件矿物暂藏 · 命中鼹鼠或风铃可立即取回`;
    return this.creatures.length?`洞穴生物 ${this.creatures.filter(c=>c.scared<=0).length} · 护盾可穿过，风铃可驱赶`:'';
  }
  clear():void {
    for(const c of this.creatures){this.release(c);this.scene.remove(c.sprite,c.marker);c.sprite.material.dispose();c.marker.geometry.dispose();c.marker.material.dispose();}
    this.creatures.length=0;
  }
  dispose():void {this.clear();}
}
